/* The $50 match popup (sections/oricle-popup.liquid, 10/1; replaces the Alia app's flow). A drawer in the
   OricleDrawer sense (focus trap, Escape, scrim, counted scroll lock, focus handed back), opened by a timer.
   The script holds no copy: every string is on the section's markup.
   · Who: shown once the delay has passed on a visible tab, never over another open drawer, the cookie banner, or
     while the visitor is typing, and only while the cart is under the cap: the cart drawer's live total
     (data-total-cents, kept current by oricle-cart.js after every add or change) is read again when it would open, and never to a visitor who signed up (here or in the footer), whom Klaviyo already knows, or who came
     from an email or text. A close holds it back for the snooze days. No floating re-open button.
   · Steps: questions, email, match, with a progress bar and Back from the second step. Each answer adds its weights
     to the models' scores (Back keeps the earlier answer until it is replaced); the top score is the match (ties:
     the first id in the tie order).
   · Signup: the address goes to one Klaviyo list through the public client endpoint (the footer's request), then
     the answers and the match are set on the profile through Klaviyo's onsite identify.
   · Events (ids only, never the address; every detail named popup_<key>): an `oricle:popup` DOM event, Shopify.analytics.publish (Customer Events),
     gtag (GA4) and Clarity. A signup is also a Meta Lead and a GA4 generate_lead. */
(() => {
  'use strict';

  const KEY = 'oricle:popup';
  const SUB = 'oricle:subscribed';
  const DAY = 864e5;
  const read = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { return null; } };
  const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private mode: the popup just shows again */ } };

  /* == Pure part (window.Oricle.popup, for tests). == */
  // "2.0:2, pro:1" → { '2.0': 2, pro: 1 }
  const parseWeights = (str) => {
    const o = {};
    String(str || '').split(',').forEach((p) => {
      const i = p.indexOf(':'); if (i < 0) return;
      const k = p.slice(0, i).trim(); const n = parseFloat(p.slice(i + 1));
      if (k && !Number.isNaN(n)) o[k] = n;
    });
    return o;
  };
  // Sum the weights; the highest total wins, ties to the first id in `order`.
  const pick = (weightsList, order) => {
    const ids = String(order || '').split(',').map((x) => x.trim()).filter(Boolean);
    const total = {}; ids.forEach((id) => { total[id] = 0; });
    weightsList.forEach((w) => Object.keys(w).forEach((id) => { if (id in total) total[id] += w[id]; }));
    let best = ids[0] || null;
    ids.forEach((id) => { if (total[id] > total[best]) best = id; });
    return best;
  };
  // A visitor we should not ask: signed up, known to Klaviyo, or here from an email or text.
  const known = (loc, cookie) => {
    if (read(SUB)) return true;
    const st = read(KEY); if (st && st.state === 'signed') return true;
    const m = String(cookie || '').match(/(?:^|; )__kla_id=([^;]+)/);
    if (m) { try { const o = JSON.parse(atob(decodeURIComponent(m[1]))); if (o && (o.$email || o.$exchange_id || o.$phone_number)) return true; } catch (e) { /* unreadable: treat as unknown */ } }
    const q = new URLSearchParams(loc.search || '');
    const MSG = /^(klaviyo|email|e-mail|sms|text)$/i;
    const fromMessage = MSG.test((q.get('utm_source') || '').trim()) || MSG.test((q.get('utm_medium') || '').trim());
    let skip = false; try { skip = sessionStorage.getItem('oricle:popup:skip') === '1'; } catch (e) { /* none */ }
    if (skip) return true;
    if (fromMessage || q.has('_kx')) { try { sessionStorage.setItem('oricle:popup:skip', '1'); } catch (e) { /* none */ } return true; }
    return false;
  };
  // Off, not hidden: a step, match, Back or decline keeps its space but is inert (oricle-popup.css).
  const off = (el, v) => { el.toggleAttribute('data-off', !!v); el.inert = !!v; };
  const snoozed = (days) => { const st = read(KEY); return !!(st && st.state === 'closed' && Date.now() - st.at < days * DAY); };
  window.Oricle = window.Oricle || {};
  window.Oricle.popup = { parseWeights, pick, known, snoozed };

  const define = () => {
    const Base = window.OricleDrawer;
    if (!Base || customElements.get('oricle-popup')) return;
    let othersOpen = 0;
    document.addEventListener('oricle:drawer:open', (e) => { if (e.target.tagName !== 'ORICLE-POPUP') othersOpen += 1; });
    document.addEventListener('oricle:drawer:close', (e) => { if (e.target.tagName !== 'ORICLE-POPUP') othersOpen = Math.max(0, othersOpen - 1); });

    class OriclePopup extends Base {
      connectedCallback() {
        super.connectedCallback();
        if (this._popBound) return; this._popBound = true;
        this.steps = Array.from(this.querySelectorAll('[data-o-pop-step]'));
        this.questions = this.steps.filter((s) => s.dataset.oPopStep === 'q');
        this.count = this.querySelector('[data-o-pop-count]');
        this.back = this.querySelector('[data-o-pop-back]');
        this.segs = Array.from(this.querySelectorAll('.o-pop__seg'));
        this.decline = this.querySelector('[data-o-decline]');
        this.answers = {}; this.weights = []; this.at = 0; this.match = null; this.signed = false;
        this.campaign = this.dataset.campaign || 'Popup';
        // Capture phase: note how it was closed before the drawer's own click handler closes it.
        this.addEventListener('click', (e) => {
          if (e.target.closest('[data-o-decline]')) this._via = 'decline';
          else if (e.target.closest('[data-o-close]')) this._via = 'close';
          else if (e.target.closest('[data-o-scrim]')) this._via = 'scrim';
        }, true);
        this.addEventListener('click', (e) => this.onClick(e));
        const form = this.querySelector('.o-pop__form');
        if (form) form.addEventListener('submit', (e) => { e.preventDefault(); this.submit(form); });
        this.show(0);
        if (this.hasAttribute('data-preview')) { this.warm('.o-pop__img'); this.open(); return; }
        if (known(location, document.cookie) || snoozed(parseFloat(this.dataset.snooze) || 7)) return;
        // A page that sells with its own offer (the advertorial's offer boxes, 10/7) marks that block with
        // data-o-popup-gate: the delay starts only once the reader has scrolled past it, so the popup never covers
        // the opening or the boxes, and never offers a second $50 before the page's own has been read.
        const gate = document.querySelector('[data-o-popup-gate]');
        if (gate) {
          this.onGate = () => {
            if (gate.getBoundingClientRect().bottom >= 0) return;
            window.removeEventListener('scroll', this.onGate);
            this.onGate = null;
            this.arm((parseFloat(this.dataset.delay) || 0) * 1000);
          };
          window.addEventListener('scroll', this.onGate, { passive: true });
          this.onGate(); // opened already past the boxes (a link to #faq, back/forward)
          return;
        }
        this.arm((parseFloat(this.dataset.delay) || 0) * 1000);
      }
      disconnectedCallback() { super.disconnectedCallback(); clearTimeout(this._armT); if (this.onGate) window.removeEventListener('scroll', this.onGate); }

      // Lazy pictures in a hidden dialog load only once it shows: fetch them ahead for a visitor who will see it.
      warm(sel) { this.querySelectorAll(sel).forEach((i) => { i.loading = 'eager'; }); }

      // The cart drawer's live total (cents); the page-load total when the drawer is not on the page.
      cartFull() {
        const cap = parseInt(this.dataset.cartMax, 10) || 0;
        if (cap <= 0) return false;
        const drawer = document.getElementById('OricleCartDrawer');
        const cents = drawer ? parseInt(drawer.dataset.totalCents, 10) : NaN;
        return !Number.isNaN(cents) && cents >= cap;
      }

      // Wait out the delay, then for a visible tab, no other drawer and no signup in progress.
      arm(ms) {
        clearTimeout(this._armT);
        this.warm('.o-pop__img--intro');
        this._armT = setTimeout(() => {
          const a = document.activeElement;
          const typing = a && a !== document.body && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName) && !this.contains(a);
          const capturing = (a && a.closest('oricle-capture')) || document.querySelector('oricle-capture[aria-busy="true"]');
          const consent = document.getElementById('shopify-pc__banner');
          const asking = consent && consent.getClientRects().length > 0 && getComputedStyle(consent).visibility !== 'hidden';
          if (known(location, document.cookie)) return; // signed up elsewhere meanwhile: stop for good
          if (this.cartFull()) return; // a hearing aid went into the cart meanwhile: not on this page
          if (document.hidden || othersOpen > 0 || typing || capturing || asking) { this.arm(2500); return; }
          this.open();
          this.emit('oricle_popup_shown', { step: this.stepName() });
        }, ms);
      }

      // The trap's list without inert controls: off steps keep their boxes (opacity, not display), so the base
      // list would end on the inert "No thanks" on the match and let Tab leave the dialog.
      focusables() { return super.focusables().filter((el) => !el.closest('[inert]')); }

      stepName() { const s = this.steps[this.at]; return s ? (s.dataset.oPopStep === 'q' ? s.dataset.key : s.dataset.oPopStep) : ''; }

      show(i, focus) {
        this.at = i;
        this.steps.forEach((s, n) => { off(s, n !== i); });
        const step = this.steps[i]; const kind = step && step.dataset.oPopStep;
        if (this.count) {
          const total = this.questions.length + 1;
          this.count.textContent = kind === 'match' ? '' : (this.count.dataset.of || '{n} of {total}').replace('{n}', String(i + 1)).replace('{total}', String(total));
        }
        if (this.decline) off(this.decline, kind === 'match');
        this.classList.toggle('is-match', kind === 'match');
        if (this.back) off(this.back, i === 0 || kind === 'match');
        this.segs.forEach((seg, n) => seg.classList.toggle('is-done', n <= i));
        // The dialog's name follows the visible heading (the first step's, the email step's, the match's model).
        const panel = this.querySelector('[role="dialog"]');
        const head = step && Array.from(step.querySelectorAll('h2')).find((h) => !h.closest('[hidden],[data-off]'));
        if (panel && head) { if (!head.id) head.id = `OriclePopHead${i}`; panel.setAttribute('aria-labelledby', head.id); }
        if (!focus || !step) return;
        const target = kind === 'email' ? step.querySelector('input')
          : (kind === 'match' ? step : (step.querySelector('[data-o-pop-opt][aria-pressed="true"]') || step.querySelector('[data-o-pop-opt]')));
        if (target) target.focus({ preventScroll: true });
      }

      onClick(e) {
        const opt = e.target.closest('[data-o-pop-opt]');
        if (opt && this.contains(opt)) { this.answer(opt); return; }
        if (e.target.closest('[data-o-pop-back]')) { this.goBack(); return; }
        if (e.target.closest('[data-o-copy]')) { this.copy(e.target.closest('[data-o-copy]')); return; }
        if (e.target.closest('[data-o-pop-shop]')) this.emit('oricle_popup_shop', { match: this.match });
      }

      answer(opt) {
        if (this._advancing) return;
        const step = opt.closest('[data-o-pop-step]');
        step.querySelectorAll('[data-o-pop-opt]').forEach((b) => b.setAttribute('aria-pressed', b === opt ? 'true' : 'false'));
        const q = step.dataset.key || `q${this.at + 1}`;
        this.answers[q] = opt.dataset.key;
        this.weights[this.at] = parseWeights(opt.dataset.w);
        this.emit('oricle_popup_answer', { question: q, answer: opt.dataset.key, step: q });
        this._advancing = true;
        setTimeout(() => {
          this._advancing = false;
          this.show(this.at + 1, true);
          if (this.steps[this.at] && this.steps[this.at].dataset.oPopStep === 'email') { this.warm('.o-pop__img--match'); this.emit('oricle_popup_email_step', { step: 'email' }); }
        }, 160);
      }

      goBack() {
        if (this._advancing || this._busy || this.at === 0 || this.stepName() === 'match') return;
        this.emit('oricle_popup_back', { from: this.stepName() });
        this.say('');
        this.show(this.at - 1, true);
      }

      say(text) { const m = this.querySelector('.o-pop__msg'); if (m) m.textContent = text || ''; }

      async submit(form) {
        const input = form.querySelector('input[type="email"]');
        const button = form.querySelector('button[type="submit"]');
        const email = (input.value || '').trim();
        input.required = true;
        if (!email || !input.checkValidity()) { input.setAttribute('aria-invalid', 'true'); input.reportValidity(); return; }
        input.removeAttribute('aria-invalid');
        if (this._busy) return; this._busy = true; if (button) button.setAttribute('aria-disabled', 'true');
        this.say('');
        this.match = pick(this.weights.filter(Boolean), this.dataset.tie);
        const body = { data: { type: 'subscription', attributes: { custom_source: this.campaign, profile: { data: { type: 'profile',
          attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } },
          relationships: { list: { data: { type: 'list', id: this.dataset.list } } } } };
        try {
          const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.dataset.company || '')}`, {
            method: 'POST', headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' }, body: JSON.stringify(body) });
          if (!res.ok) throw new Error(String(res.status));
        } catch (err) {
          input.setAttribute('aria-invalid', 'true');
          this.say(this.dataset.error || 'We could not sign you up just now. Please try again.');
          this.emit('oricle_popup_signup_error', {});
          this._busy = false; if (button) button.removeAttribute('aria-disabled');
          return;
        }
        this._busy = false; if (button) button.removeAttribute('aria-disabled');
        this.signed = true;
        write(KEY, { state: 'signed', at: Date.now() }); write(SUB, 1);
        this.profile(email);
        this.emit('oricle_popup_signup', { match: this.match });
        try { if (typeof window.fbq === 'function') window.fbq('track', 'Lead', { content_name: this.campaign, content_category: this.match }); } catch (e) { /* never blocks */ }
        try { if (typeof window.gtag === 'function') window.gtag('event', 'generate_lead', { lead_source: this.campaign, popup_match: this.match }); } catch (e) { /* never blocks */ }
        this.reveal();
      }

      // The answers and the match on the Klaviyo profile, for segments and the welcome flow.
      profile(email) {
        try {
          const k = window.klaviyo; if (!k || typeof k.identify !== 'function') return;
          const props = { email, 'Popup match': this.match, 'Popup source': this.campaign };
          Object.keys(this.answers).forEach((q) => { props[`Popup ${q}`] = this.answers[q]; });
          k.identify(props);
        } catch (e) { /* never blocks */ }
      }

      reveal() {
        const step = this.steps.findIndex((s) => s.dataset.oPopStep === 'match');
        this.querySelectorAll('[data-o-pop-match]').forEach((m) => { off(m, m.dataset.oPopMatch !== this.match); });
        const img = this.querySelector(`[data-o-pop-img="${this.match}"]`);
        if (img) { this.querySelectorAll('.o-pop__img').forEach((i) => { i.hidden = i !== img; }); }
        this.show(step, true);
      }

      async copy(btn) {
        const code = this.querySelector('[data-o-code]'); if (!code) return;
        const text = code.textContent.trim(); let ok = false;
        try { await navigator.clipboard.writeText(text); ok = true; } catch (e) { /* select-and-copy below */ }
        if (!ok) {
          const r = document.createRange(); r.selectNodeContents(code);
          const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(r);
          try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
          if (!ok) return;
        }
        if (!btn._label) btn._label = btn.textContent;
        btn.textContent = btn.dataset.copied || 'Copied';
        clearTimeout(this._copyT); this._copyT = setTimeout(() => { btn.textContent = btn._label; }, 2000);
        this.emit('oricle_popup_code_copy', { match: this.match });
      }

      handleKey(e) { if (this.isOpen && e.key === 'Escape') this._via = 'escape'; super.handleKey(e); }

      close() {
        if (!this.isOpen) return;
        const step = this.stepName(); const via = this._via || 'close'; this._via = null;
        super.close();
        if (this.hasAttribute('data-preview')) return;
        if (this.signed) { this.emit('oricle_popup_close', { step, match: this.match }); return; }
        write(KEY, { state: 'closed', at: Date.now() });
        this.emit('oricle_popup_dismiss', { step, via });
      }

      // Every detail goes out as popup_<key> (popup_step, popup_match …): GA4 custom dimensions map one parameter name
      // across all events, so plain names like step or campaign would mix with other events and gtag's own fields.
      emit(name, data) {
        const detail = { popup_campaign: this.campaign, popup_guidance_version: this.dataset.guidanceVersion || 'legacy' };
        Object.keys(data || {}).forEach((k) => { detail[`popup_${k}`] = data[k]; });
        this.dispatchEvent(new CustomEvent('oricle:popup', { bubbles: true, detail: Object.assign({ event: name }, detail) }));
        try { if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') window.Shopify.analytics.publish(name, detail); } catch (e) { /* never blocks */ }
        try { if (typeof window.gtag === 'function') window.gtag('event', name, detail); } catch (e) { /* never blocks */ }
        try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (e) { /* never blocks */ }
      }
    }
    customElements.define('oricle-popup', OriclePopup);
  };
  if (window.OricleDrawer) define();
  else document.addEventListener('DOMContentLoaded', define);
})();
